import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, ArrowUpRight, TrendingUp, TrendingDown } from "lucide-react";
import { cachedGet } from "../../lib/apiCache";
import { inr } from "./shared";

const BUCKET_TONE = { "0-30": "bg-emerald-400", "31-60": "bg-amber-400", "61-90": "bg-orange-500", "90+": "bg-red-600" };

function Stat({ label, value, sub, tone = "text-white" }) {
  return (
    <div className="bg-white/[0.05] border border-white/10 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-bold">{label}</div>
      <div className={`font-heading font-black text-lg mt-0.5 ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export function BillingDashboardTile() {
  const [d, setD] = useState(null);
  useEffect(() => { cachedGet("/billing/reports/dashboard", null, { ttlMs: 60_000 }).then(setD).catch(() => setD(false)); }, []);
  if (d === false) return null;
  if (!d) return <div className="h-[260px] rounded-3xl bg-slate-900 animate-pulse" data-testid="dashboard-billing-loading" />;
  const delta = d.prev_month_billed ? Math.round(((d.month_billed - d.prev_month_billed) / d.prev_month_billed) * 100) : null;
  const total = Object.values(d.buckets || {}).reduce((s, b) => s + (b.amount || 0), 0) || 1;
  return (
    <section className="relative overflow-hidden bg-slate-950 rounded-3xl p-4 sm:p-6 text-white" data-testid="dashboard-billing-tile">
      <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-red-700/25 blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-red-300 font-bold flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" /> Billing · this month</div>
          <div className="font-heading font-black text-2xl sm:text-3xl mt-1" data-testid="dashboard-billing-month-billed">{inr(d.month_billed)}</div>
          <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
            {d.month_invoices} invoices
            {delta !== null && <span className={`inline-flex items-center gap-0.5 font-semibold ${delta >= 0 ? "text-emerald-300" : "text-red-300"}`}>{delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(delta)}% vs last month</span>}
          </div>
        </div>
        <Link to="/billing" className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold transition-colors" data-testid="dashboard-billing-open">Open Billing <ArrowUpRight className="h-3.5 w-3.5" /></Link>
      </div>
      <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mt-4">
        <Stat label="Received" value={inr(d.month_received)} sub={`${d.month_receipts} receipts`} tone="text-emerald-300" />
        <Stat label="GST this month" value={inr(d.month_gst)} tone="text-sky-300" />
        <Stat label="Outstanding" value={inr(d.outstanding)} sub={`${d.unpaid_count} unpaid bills`} tone="text-red-300" />
        <Stat label="On account" value={inr(d.on_account)} sub="unlinked receipts" tone="text-amber-200" />
      </div>
      <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-bold mb-2">Ageing of dues</div>
          <div className="flex h-3 rounded-full overflow-hidden bg-white/10">
            {Object.entries(d.buckets || {}).map(([k, b]) => <div key={k} className={BUCKET_TONE[k]} style={{ width: `${(b.amount / total) * 100}%` }} title={`${k} days: ${inr(b.amount)}`} />)}
          </div>
          <div className="grid grid-cols-4 gap-1 mt-2 text-[11px]">
            {Object.entries(d.buckets || {}).map(([k, b]) => (
              <div key={k}><div className="flex items-center gap-1 text-slate-400"><span className={`h-2 w-2 rounded-full ${BUCKET_TONE[k]}`} />{k}d</div><div className="font-semibold">{inr(b.amount)}</div></div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-bold mb-2">Largest dues</div>
          {(d.top_dues || []).length === 0 ? <div className="text-xs text-slate-500 italic">All bills settled.</div> : (
            <div className="space-y-1">
              {d.top_dues.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-xs bg-white/[0.04] rounded-lg px-2.5 py-1.5" data-testid={`dashboard-billing-due-${t.id}`}>
                  <span className="truncate"><span className="font-mono text-slate-400">{t.invoice_number}</span> · {t.customer_name}</span>
                  <span className="shrink-0 font-semibold text-red-300">{inr(t.balance_due)} <span className="text-slate-500 font-normal">· {t.days_old}d</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
